/**
 * Calendar-date arithmetic on `YYYY-MM-DD` strings, done in UTC so daylight
 * saving never shifts a day. No date library; the Calendar picker is the only user.
 */

export interface CalendarDate {
  year: number;
  /** 1-12 */
  month: number;
  /** 1-31 */
  day: number;
}

const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

export function parseIsoDate(value: string): CalendarDate | null {
  const match = ISO_DATE.exec(value);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return null;
  return { year, month, day };
}

function pad(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function toIsoDate({ year, month, day }: CalendarDate): string {
  return `${year}-${pad(month)}-${pad(day)}`;
}

export function daysInMonth(year: number, month: number): number {
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

/** 0 = Sunday ... 6 = Saturday */
export function weekday(date: CalendarDate): number {
  return new Date(Date.UTC(date.year, date.month - 1, date.day)).getUTCDay();
}

export function addDays(date: CalendarDate, days: number): CalendarDate {
  const d = new Date(Date.UTC(date.year, date.month - 1, date.day + days));
  return { year: d.getUTCFullYear(), month: d.getUTCMonth() + 1, day: d.getUTCDate() };
}

/** Adds months, clamping the day to the target month's length. */
export function addMonths(date: CalendarDate, months: number): CalendarDate {
  const total = date.year * 12 + (date.month - 1) + months;
  const year = Math.floor(total / 12);
  const month = (total % 12) + 1;
  return { year, month, day: Math.min(date.day, daysInMonth(year, month)) };
}

export function sameMonth(a: CalendarDate, b: CalendarDate): boolean {
  return a.year === b.year && a.month === b.month;
}

/**
 * The dates shown in a month grid: complete weeks from the first week containing
 * the 1st to the last week containing the final day, always 6 rows so the grid
 * never changes height.
 */
export function monthGrid(year: number, month: number, weekStartsOn: number): CalendarDate[] {
  const first: CalendarDate = { year, month, day: 1 };
  const offset = (weekday(first) - weekStartsOn + 7) % 7;
  const start = addDays(first, -offset);
  const cells: CalendarDate[] = [];
  for (let i = 0; i < 42; i += 1) cells.push(addDays(start, i));
  return cells;
}
